import apiClient from './client'
import type { Dataset, CreateDatasetDto, DatasetPreview, DatasetSyncInfo } from '@/types/dataset'

export const datasetsApi = {
  list: async (options?: {
    limit?: number;
    offset?: number;
    status?: string;
    search?: string;
    dateFrom?: string;
    dateTo?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }): Promise<{
    datasets: Dataset[]
    pagination: {
      total: number
      limit: number
      offset: number
      hasMore: boolean
    }
  }> => {
    const params = new URLSearchParams()
    if (options?.limit) params.append('limit', options.limit.toString())
    if (options?.offset) params.append('offset', options.offset.toString())
    if (options?.status) params.append('status', options.status)
    if (options?.search) params.append('search', options.search)
    if (options?.dateFrom) params.append('dateFrom', options.dateFrom)
    if (options?.dateTo) params.append('dateTo', options.dateTo)
    if (options?.sortBy) params.append('sortBy', options.sortBy)
    if (options?.sortOrder) params.append('sortOrder', options.sortOrder)
    
    const response = await apiClient.get(`/datasets?${params.toString()}`)
    const data = response.data
    
    // Handle both old format (array) and new format (object with datasets and pagination)
    if (Array.isArray(data)) {
      return {
        datasets: data,
        pagination: {
          total: data.length,
          limit: data.length,
          offset: 0,
          hasMore: false,
        },
      }
    }
    
    if (data?.datasets && Array.isArray(data.datasets)) {
      return {
        datasets: data.datasets,
        pagination: data.pagination || {
          total: data.datasets.length,
          limit: data.datasets.length,
          offset: 0,
          hasMore: false,
        },
      }
    }
    
    return {
      datasets: [],
      pagination: {
        total: 0,
        limit: 0,
        offset: 0,
        hasMore: false,
      },
    }
  },

  get: async (id: string): Promise<Dataset> => {
    const response = await apiClient.get(`/datasets/${id}`)
    return response.data
  },

  upload: async (data: CreateDatasetDto): Promise<Dataset> => {
    const formData = new FormData()
    formData.append('file', data.file)
    formData.append('name', data.name)
    
    if (data.metadata) {
      // Extract top-level fields that backend expects
      if (data.metadata.symbol) {
        formData.append('symbol', data.metadata.symbol)
      }
      if (data.metadata.timeframe) {
        formData.append('timeframe', data.metadata.timeframe)
      }
      if (data.metadata.timezone) {
        formData.append('timezone', data.metadata.timezone)
      }
      
      // Extract columnMapping and send as JSON string (backend will parse it)
      if (data.metadata.columnMapping) {
        formData.append('columnMapping', JSON.stringify(data.metadata.columnMapping))
      }
    }

    const response = await apiClient.post('/datasets', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return response.data
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/datasets/${id}`)
  },

  preview: async (id: string): Promise<DatasetPreview> => {
    const response = await apiClient.get(`/datasets/${id}/preview`)
    return response.data
  },

  previewColumns: async (file: File): Promise<{ columns: string[]; rowCount: number }> => {
    const formData = new FormData()
    formData.append('file', file)

    const response = await apiClient.post('/datasets/preview-columns', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return response.data
  },

  regenerate: async (id: string, metadata: any, csvMapping: any): Promise<{ success: boolean; message: string; metadataHash: string }> => {
    const response = await apiClient.post(`/datasets/${id}/regenerate`, {
      metadata,
      csvMapping
    })
    return response.data
  },

  getSyncStatus: async (id: string): Promise<DatasetSyncInfo> => {
    const response = await apiClient.get(`/datasets/${id}/sync-status`)
    return response.data
  },
}
